import {flow, getParent} from 'mobx-state-tree';
import AuthModel from '../models/auth-model';
import {getCurrentSession, logIn, setLocalSession} from '../services/authService';
import UserService from '../services/user-service';
import DeviceService from '../services/device-service';
import {getProfile} from '../helpers/auth';
import {queryClient} from 'src/autobahn/infra/QueryClient';
import {companyKeys} from 'src/autobahn/features/company/queries';

const AuthStore = AuthModel
  .views((self) => ({
    getUser() {
      return self.user;
    },
    get token() {
      return btoa(`${self.email}:${self.password}`);
    },
}))
.actions((self) => ({
    setUser: (data) => {
        self.user = data;
    },
}))
.actions((self) => ({
    revalidateUser: flow(function* () {
        if (self.user) {
          try {
            const _user = yield getCurrentSession();
            self.user = _user;

            const deviceService = new DeviceService();
            const devices = yield deviceService.getDevices();

            self.setProfile(getProfile({
              ...self.user,
              devicesLength: devices.length,
            }));
          } catch (e) {
            // session refresh or device fetch failed — keep current user/profile,
            // next revalidateUser (e.g. next page boot) will retry
          }
        }
    }),
}))
.actions((self) => ({
    afterCreate: flow(function* () {
        yield self.revalidateUser();
    }),

    setLogged: (value) => (self.isLogged = value),

    setProfile: (profile) => (self.profile = profile),

    setAuthInfo: (email, password) => {
      self.email = email;
      self.password = password;
    },

    setUser: (data) => {
      self.user = data;
    },
  }))
  .actions((self) => ({
    signIn: flow(function* (data) {
      const response = yield logIn(data);
      self.setAuthInfo(data.email, data.password);
      self.setUser(response);

      const globalStore = getParent(self);
      // login na mesma aba sem passar pelo logOut (token expirado, /login digitado
      // direto) nao recarrega a pagina: sem isto a conta nova herda o painel da anterior
      globalStore.events.removeAll();
      globalStore.setSession(response);
      setLocalSession(self.token);
      // Login na mesma aba não recarrega a página: sem isto a conta nova veria, até a consulta responder, a
      // central e a fila de relatórios da anterior. A marca do domínio não depende da conta e fica, senão a
      // próxima leitura dela deixaria o app em branco.
      const domainBrandKey = companyKeys.resolve();
      queryClient.removeQueries({
        predicate: (query) => !domainBrandKey.every((part, i) => query.queryKey[i] === part),
      });
      globalStore.map.setCenter({
        longitude: self.user?.longitude,
        latitude: self.user?.latitude,
      });
      globalStore.map.setZoom(self.user?.zoom);

      yield Promise.all([
        globalStore.devices.loadDevices(),
        globalStore.syncExternalApi(),
      ]);

      self.setProfile(getProfile({
        ...response,
        devicesLength: globalStore.devices.devices.length,
        auth: data,
      }));

      self.setLogged(true);
    }),

    updateUser: flow(function* (data) {
        const userService = new UserService();
        const updatedUser = yield userService.update({
            ...self.user,
            ...data,
        });
        self.setUser(updatedUser);
    }),
}));

export default AuthStore;
